<template>
    <!-- Breadcrumb Navigation -->
    <div class="tw-p-2 tw-bg-white tw-w-full tw-border tw-border-solid tw-flex tw-items-center tw-gap-2">
        <router-link :to="{ name: 'pages.admin.dashboard' }"
            class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <icon icon="solar:home-smile-bold-duotone" /> 
            <span class="tw-text-sm">{{ $t("dashboard.sidebar.dashboard") }}</span>
        </router-link>
        <span class="tw-text-gray-300">/</span>
        <router-link :to="{ name: 'pages.admin.products' }"
            class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-sm">{{ $t("dashboard.sidebar.products") }}</span>
        </router-link>
        <span class="tw-text-gray-300">/</span>
        <button disabled class="tw-text-primary-500 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-sm">{{ $t("common.edit") }}</span>
        </button>
    </div>

    <!-- Main Content -->
    <div class="tw-p-2">
        <div class="tw-w-full tw-h-full tw-bg-white tw-rounded-lg tw-p-6 tw-border tw-border-solid tw-border-gray-200">
            
            <!-- Loading State with Skeleton -->
            <div v-if="loading">
                <!-- Header Skeleton -->
                <div class="tw-flex tw-items-center tw-justify-between tw-mb-8">
                    <div>
                        <div class="tw-w-48 tw-h-8 tw-bg-gray-200 tw-rounded-md tw-animate-pulse tw-mb-2"></div>
                        <div class="tw-w-72 tw-h-4 tw-bg-gray-200 tw-rounded-md tw-animate-pulse"></div>
                    </div>
                    <div class="tw-w-32 tw-h-10 tw-bg-gray-200 tw-rounded-md tw-animate-pulse"></div>
                </div>

                <!-- Form Skeleton -->
                <div class="tw-w-full">
                    <div class="tw-grid tw-grid-cols-1 xl:tw-grid-cols-12 tw-gap-8">
                        <!-- Left Column Skeleton - Product Information -->
                        <div class="xl:tw-col-span-8 tw-space-y-6">
                            <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                                <!-- Product Information Header -->
                                <div class="tw-w-48 tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-6"></div>
                                
                                <div class="tw-space-y-6">
                                    <!-- Product Title Field Skeleton -->
                                    <div>
                                        <div class="tw-w-28 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-2"></div>
                                        <div class="tw-w-full tw-h-12 tw-bg-gray-200 tw-rounded-md tw-animate-pulse tw-mb-1"></div>
                                        <div class="tw-w-72 tw-h-3 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                    </div>

                                    <!-- Description Field Skeleton -->
                                    <div>
                                        <div class="tw-w-24 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-2"></div>
                                        <div class="tw-w-full tw-h-32 tw-bg-gray-200 tw-rounded-md tw-animate-pulse tw-mb-1"></div>
                                        <div class="tw-w-80 tw-h-3 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                    </div>

                                    <!-- Categories Field Skeleton -->
                                    <div>
                                        <div class="tw-w-20 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-2"></div>
                                        <div class="tw-w-full tw-h-12 tw-bg-gray-200 tw-rounded-md tw-animate-pulse tw-mb-1"></div>
                                        <div class="tw-w-64 tw-h-3 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                    </div>
                                </div>
                            </div>

                            <!-- Company Information Skeleton -->
                            <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                                <!-- Company Header -->
                                <div class="tw-w-40 tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-4"></div>
                                
                                <!-- Company Content -->
                                <div class="tw-flex tw-items-center tw-gap-4">
                                    <!-- Company Logo Skeleton -->
                                    <div class="tw-w-16 tw-h-16 tw-bg-gray-200 tw-rounded-full tw-animate-pulse tw-flex-shrink-0"></div>
                                    
                                    <!-- Company Details Skeleton -->
                                    <div class="tw-flex-1">
                                        <div class="tw-w-48 tw-h-5 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-2"></div>
                                        <div class="tw-w-32 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Right Column Skeleton - Product Image -->
                        <div class="xl:tw-col-span-4 tw-space-y-6">
                            <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                                <!-- Image Section Header -->
                                <div class="tw-w-32 tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-6"></div>
                                
                                <!-- Image Label -->
                                <div class="tw-w-36 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-3"></div>
                                
                                <!-- Image Upload Area Skeleton -->
                                <div class="tw-w-full tw-h-80 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse tw-flex tw-items-center tw-justify-center tw-mb-6">
                                    <div class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center">
                                        <div class="tw-w-16 tw-h-16 tw-bg-gray-300 tw-rounded-full tw-animate-pulse tw-mb-4"></div>
                                        <div class="tw-w-40 tw-h-5 tw-bg-gray-300 tw-rounded tw-animate-pulse tw-mb-2"></div>
                                        <div class="tw-w-48 tw-h-4 tw-bg-gray-300 tw-rounded tw-animate-pulse tw-mb-4"></div>
                                        <div class="tw-w-28 tw-h-8 tw-bg-gray-300 tw-rounded tw-animate-pulse tw-mb-3"></div>
                                        <div class="tw-w-32 tw-h-3 tw-bg-gray-300 tw-rounded tw-animate-pulse"></div>
                                    </div>
                                </div>

                                <!-- Image Tips Skeleton -->
                                <div class="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4">
                                    <div class="tw-w-24 tw-h-4 tw-bg-blue-200 tw-rounded tw-animate-pulse tw-mb-2"></div>
                                    <div class="tw-space-y-1">
                                        <div class="tw-w-full tw-h-3 tw-bg-blue-200 tw-rounded tw-animate-pulse"></div>
                                        <div class="tw-w-5/6 tw-h-3 tw-bg-blue-200 tw-rounded tw-animate-pulse"></div>
                                        <div class="tw-w-full tw-h-3 tw-bg-blue-200 tw-rounded tw-animate-pulse"></div>
                                        <div class="tw-w-4/5 tw-h-3 tw-bg-blue-200 tw-rounded tw-animate-pulse"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Form Actions Skeleton -->
                    <div class="tw-border-t tw-border-gray-200 tw-pt-6 tw-mt-8">
                        <div class="tw-flex tw-items-center tw-gap-3">
                            <div class="tw-w-24 tw-h-10 tw-bg-gray-200 tw-rounded-md tw-animate-pulse"></div>
                            <div class="tw-w-44 tw-h-10 tw-bg-gray-200 tw-rounded-md tw-animate-pulse"></div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Content -->
            <div v-else>
                <!-- Header Section -->
                <div class="tw-flex tw-items-center tw-justify-between tw-mb-8">
                    <div>
                        <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">Edit Product</h1>
                        <p class="tw-text-sm tw-text-gray-600">Update information for "{{ originalProduct?.name || 'Loading...' }}"</p>
                    </div>
                    
                    <!-- Back Button -->
                    <a-button
                        @click="goBack"
                        intent="light"
                        left-icon="solar:arrow-left-linear"
                    >
                        Back to Products
                    </a-button>
                </div>

                <!-- Form Content -->
                <form @submit.prevent="updateProduct" class="tw-w-full">
                    <div class="tw-grid tw-grid-cols-1 xl:tw-grid-cols-12 tw-gap-8">
                        <!-- Left Column - Product Information -->
                        <div class="xl:tw-col-span-8 tw-space-y-6">
                            <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                                <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-6">Product Information</h2>
                                
                                <div class="tw-space-y-6">
                                    <!-- Product Title -->
                                    <FormField
                                        v-model="productForm.title"
                                        type="text"
                                        label="Product Title"
                                        placeholder="Enter product title"
                                        left-icon="solar:box-linear"
                                        :error="productErrors.title"
                                        required
                                        help="Enter a clear, descriptive title for your product"
                                    />

                                    <!-- Product Description -->
                                    <FormField
                                        v-model="productForm.description"
                                        type="textarea"
                                        label="Description"
                                        placeholder="Enter detailed product description"
                                        :error="productErrors.description"
                                        rows="6"
                                        help="Provide a detailed description of your product features and benefits"
                                    />

                                    <!-- Categories -->
                                    <FormMultiSelect
                                        v-model="productForm.categories"
                                        :options="categoryOptions"
                                        label="Categories"
                                        placeholder="Select product categories"
                                        left-icon="solar:widget-bold-duotone"
                                        :error="productErrors.categories"
                                        help="Choose relevant categories to help customers find your product"
                                        :loading="categoriesLoading"
                                    />
                                </div>
                            </div>

                            <!-- Company Information (Read-only) -->
                            <div v-if="originalProduct" class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                                <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">Company Information</h3>
                                <div class="tw-flex tw-items-center tw-gap-4">
                                    <!-- Company Logo -->
                                    <div class="tw-w-16 tw-h-16 tw-flex-shrink-0">
                                        <img
                                            v-if="originalProduct.company_logo_url"
                                            :src="originalProduct.company_logo_url"
                                            :alt="originalProduct.company_name"
                                            class="tw-w-16 tw-h-16 tw-rounded-full tw-object-contain tw-border-2 tw-border-gray-200"
                                        />
                                        <div
                                            v-else
                                            class="tw-w-16 tw-h-16 tw-bg-gradient-to-br tw-from-primary-500 tw-to-primary-600 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-2 tw-border-gray-200"
                                        >
                                            <span class="tw-text-white tw-text-lg tw-font-semibold">
                                                {{ getCompanyInitials(originalProduct.company_name) }}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <!-- Company Details -->
                                    <div class="tw-flex-1">
                                        <div class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-1">
                                            {{ originalProduct.company_name || 'No company assigned' }}
                                        </div>
                                        <div class="tw-text-sm tw-text-gray-600">
                                            Company ID: {{ originalProduct.company_id || 'N/A' }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Right Column - Product Image -->
                        <div class="xl:tw-col-span-4 tw-space-y-6">
                            <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                                <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-6">Product Image</h2>
                                
                                <!-- Image Upload Area -->
                                <div class="tw-mb-6">
                                    <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-3">
                                        Product Image <span class="tw-text-gray-500">(Optional)</span>
                                    </label>
                                    
                                    <div
                                        @drop.prevent="handleImageDrop"
                                        @dragover.prevent="isDragging = true"
                                        @dragleave.prevent="isDragging = false"
                                        @click="triggerImageInput"
                                        :class="[
                                            'tw-relative tw-w-full tw-h-80 tw-border-2 tw-border-dashed tw-rounded-lg tw-cursor-pointer tw-transition-all tw-duration-200 tw-flex tw-items-center tw-justify-center',
                                            isDragging ? 'tw-border-primary-500 tw-bg-primary-50' : 'tw-border-gray-300 hover:tw-border-primary-400 tw-bg-gray-50 hover:tw-bg-gray-100',
                                            productForm.imagePreview ? 'tw-border-solid tw-bg-white' : ''
                                        ]"
                                    >
                                        <!-- Image Preview -->
                                        <div v-if="productForm.imagePreview" class="tw-relative tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
                                            <img
                                                :src="productForm.imagePreview"
                                                alt="Product preview"
                                                class="tw-max-w-full tw-max-h-full tw-object-contain tw-rounded-lg"
                                            />
                                            <button
                                                @click.stop="removeImage"
                                                type="button"
                                                class="tw-absolute tw-top-3 tw-right-3 tw-w-10 tw-h-10 tw-bg-red-500 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center hover:tw-bg-red-600 tw-transition-colors tw-shadow-lg"
                                            >
                                                <icon icon="solar:close-circle-bold" class="tw-text-xl" />
                                            </button>
                                        </div>
                                        
                                        <!-- Upload Placeholder - Centered -->
                                        <div v-else class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-p-8">
                                            <div class="tw-mb-4">
                                                <icon icon="solar:camera-add-bold-duotone" class="tw-text-6xl tw-text-gray-400" />
                                            </div>
                                            <h3 class="tw-text-lg tw-font-medium tw-text-gray-700 tw-mb-2">
                                                Upload Product Image
                                            </h3>
                                            <p class="tw-text-sm tw-text-gray-500 tw-mb-4 tw-max-w-xs">
                                                Drop an image here or click to browse
                                            </p>
                                            <div class="tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-bg-primary-500 tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-primary-600 tw-transition-colors">
                                                <icon icon="solar:upload-bold-duotone" class="tw-mr-2" />
                                                Choose Image
                                            </div>
                                            <p class="tw-text-xs tw-text-gray-400 tw-mt-3">
                                                JPG, PNG, GIF up to 5MB
                                            </p>
                                        </div>
                                        
                                        <input
                                            ref="imageInput"
                                            type="file"
                                            accept="image/*"
                                            @change="handleImageSelect"
                                            class="tw-hidden"
                                        />
                                    </div>
                                    
                                    <p v-if="productErrors.thumbnail_url" class="tw-text-red-500 tw-text-sm tw-mt-2">
                                        {{ productErrors.thumbnail_url }}
                                    </p>
                                </div>

                                <!-- Image Upload Tips -->
                                <div class="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4">
                                    <h3 class="tw-text-sm tw-font-medium tw-text-blue-900 tw-mb-2 tw-flex tw-items-center">
                                        <icon icon="solar:info-circle-bold" class="tw-mr-2" />
                                        Image Tips
                                    </h3>
                                    <ul class="tw-text-sm tw-text-blue-800 tw-space-y-1">
                                        <li>• Use high-quality images for better presentation</li>
                                        <li>• Square or landscape orientation works best</li>
                                        <li>• Ensure good lighting and clear product visibility</li>
                                        <li>• Avoid images with heavy text overlays</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Form Actions -->
                    <div class="tw-border-t tw-border-gray-200 tw-pt-6 tw-mt-8">
                        <div class="tw-flex tw-items-center tw-gap-3">
                            <!-- Cancel Button -->
                            <a-button
                                type="button"
                                intent="white"
                                @click="goBack"
                                :disabled="savingProduct"
                            >
                                Cancel
                            </a-button>
                            
                            <!-- Update Product Button -->
                            <a-button
                                type="submit"
                                intent="primary"
                                :loading="savingProduct"
                                :disabled="!productForm.title || savingProduct"
                                left-icon="solar:pen-bold-duotone"
                                class="tw-min-w-[180px]"
                            >
                                Update Product
                            </a-button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import FormField from '@/components/form/FormField.vue'
import FormMultiSelect from '@/components/form/FormMultiSelect.vue'
import useAlert from '@/composables/useAlert'
import productApi from '@/api/products'
import appApi from '@/api/app'

const router = useRouter()
const route = useRoute()

// Form state
const loading = ref(true)
const savingProduct = ref(false)
const originalProduct = ref(null)
const categoriesLoading = ref(false)
const categories = ref([])
const isDragging = ref(false)
const imageInput = ref(null)

// Product form
const productForm = reactive({
    title: '',
    description: '',
    image: null,
    imagePreview: '',
    categories: []
})

const productErrors = reactive({
    title: '',
    description: '',
    thumbnail_url: '',
    categories: ''
})

// Computed
const categoryOptions = computed(() => {
    return categories.value.map(cat => ({
        id: cat.id,
        name: cat.name
    }))
})

// Image handling methods
const triggerImageInput = () => {
    imageInput.value?.click()
}

const handleImageSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
        processSelectedImage(file)
    }
}

const handleImageDrop = (event) => {
    isDragging.value = false
    const file = event.dataTransfer.files[0]
    if (file && file.type.startsWith('image/')) {
        processSelectedImage(file)
    }
}

const processSelectedImage = (file) => {
    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
        useAlert('Image file size must be less than 5MB', 'warning')
        return
    }

    productForm.image = file
    
    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
        productForm.imagePreview = e.target.result
    }
    reader.readAsDataURL(file)
}

const removeImage = () => {
    productForm.image = null
    productForm.imagePreview = originalProduct.value?.thumbnail_url || ''
    if (imageInput.value) {
        imageInput.value.value = ''
    }
}

const getCompanyInitials = (companyName) => {
    if (!companyName) return '?'
    
    const words = companyName.trim().split(' ')
    if (words.length === 1) {
        return words[0].charAt(0).toUpperCase()
    }
    
    return words
        .slice(0, 2) // Take first 2 words
        .map(word => word.charAt(0).toUpperCase())
        .join('')
}

// Methods
const goBack = () => {
    router.push({ name: 'pages.admin.products' })
}

const fetchCategories = async () => {
    categoriesLoading.value = true
    
    try {
        const response = await appApi.forms.categories('product')
        categories.value = response.data || []
    } catch (error) {
        console.error('Error fetching categories:', error)
        categories.value = []
    } finally {
        categoriesLoading.value = false
    }
}

const loadProduct = async () => {
    loading.value = true
    
    try {
        const productId = route.params.id
        
        // Use admin edit endpoint if available, otherwise use regular edit endpoint
        const response = productApi.admin && productApi.admin.edit
            ? await productApi.admin.edit(productId)
            : await productApi.edit(productId)
        
        if (response.data) {
            const product = response.data.data

            // Store original product data
            originalProduct.value = product

            // Populate form
            productForm.title = product.name || ''
            productForm.description = product.description || ''
            productForm.imagePreview = product.thumbnail_url || ''
            productForm.categories = product.categories || []
        } else {
            throw new Error('Product not found')
        }
    } catch (error) {
        console.error('Error loading product:', error)
        useAlert('Error loading product. Please try again.', 'danger')
        goBack()
    } finally {
        loading.value = false
    }
}

const validateProductForm = () => {
    // Reset errors
    Object.keys(productErrors).forEach(key => {
        productErrors[key] = ''
    })

    let isValid = true

    // Title validation
    if (!productForm.title.trim()) {
        productErrors.title = 'Product title is required'
        isValid = false
    } else if (productForm.title.trim().length < 3) {
        productErrors.title = 'Product title must be at least 3 characters'
        isValid = false
    } else if (productForm.title.trim().length > 100) {
        productErrors.title = 'Product title must be less than 100 characters'
        isValid = false
    }

    // Description validation
    if (productForm.description.trim().length > 1000) {
        productErrors.description = 'Description must be less than 1000 characters'
        isValid = false
    }

    return isValid
}

const updateProduct = async () => {
    if (!validateProductForm()) {
        useAlert('Please fix the form errors', 'warning')
        return
    }

    savingProduct.value = true

    try {
        const productId = route.params.id
        
        const formData = new FormData()
        formData.append('name', productForm.title.trim())
        formData.append('description', productForm.description.trim())
        
        if (productForm.image) {
            formData.append('thumbnail', productForm.image)
        }
        
        if (productForm.categories.length > 0) {
            formData.append('category_ids', JSON.stringify(productForm.categories.map(category => category.id)))
        }

        // Call the API - use admin endpoint if available
        const response = productApi.admin && productApi.admin.update 
            ? await productApi.admin.update(productId, formData)
            : await productApi.update(productId, formData)
        
        if (response.status === 200 || response.status === 201) {
            useAlert('Product updated successfully!', 'success')
            router.push({ name: 'pages.admin.products' })
        } else {
            throw new Error('Failed to update product')
        }

    } catch (error) {
        console.error('Error updating product:', error)
        
        if (error.response?.status === 422) {
            // Handle validation errors from server
            const serverErrors = error.response.data.errors
            Object.keys(serverErrors).forEach(key => {
                if (Object.prototype.hasOwnProperty.call(productErrors, key)) {
                    productErrors[key] = serverErrors[key][0]
                }
            })
            useAlert('Please fix the form errors', 'warning')
        } else {
            useAlert('Error updating product. Please try again.', 'danger')
        }
    } finally {
        savingProduct.value = false
    }
}

// Initialize
onMounted(async () => {
    await Promise.all([
        fetchCategories(),
        loadProduct()
    ])
})
</script>

<style scoped>
/* Additional custom styles if needed */
</style>